import { ActionIcon, Box, Button, Divider, ScrollArea, Tooltip } from "@mantine/core";
import { useClickOutside } from "@mantine/hooks";
import { useEffect, useState } from "react";

import AppSettings from "src/AppSettings";
import Icon from "src/components/common/Icon";
import HelpTourTarget from "src/components/help/HelpTourTarget";
import type { ILayoutLink, ILayoutSubMenu } from "src/contexts/layouts/LayoutContext";
import { helpTourTargets } from "src/help/helpTours";

import NavMenu from "./NavMenu";
import SubMenu from "./SubMenu";

/**
 * Defines the inputs for this navigation presentation.
 */
interface ISubMenusProps {
    subMenus: ILayoutSubMenu[];
    collapsed: boolean;
    menuItems?: ILayoutLink[];
    mobileOpened: boolean;
    onNavigate: () => void;
}

export const expandedSubMenuWidth = 280;
export const collapsedSubMenuWidth = 45;

/**
 * Returns zero for no submenu levels, otherwise the configured collapsed or expanded width.
 */
export const getSubMenusWidth = (subMenus: ILayoutSubMenu[], collapsed: boolean) => {
    if (subMenus.length === 0) {
        return 0;
    }

    return collapsed ? collapsedSubMenuWidth : expandedSubMenuWidth;
};

/**
 * Defines the inputs for this navigation presentation.
 */
interface IBackButtonProps {
    label: string;
    collapsed?: boolean;
    onClick: () => void;
}

/**
 * Renders a back action as a tooltip icon when collapsed or a labeled button otherwise.
 */
const BackButton = ({ label, collapsed = false, onClick }: IBackButtonProps) => {
    const icon = <Icon icon={AppSettings.icons.arrowLeft} />;

    if (collapsed) {
        return (
            <Tooltip
                label={label}
                position="right"
                withArrow
            >
                <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="lg"
                    w="100%"
                    bdrs={0}
                    onClick={onClick}
                    aria-label={label}
                >
                    {icon}
                </ActionIcon>
            </Tooltip>
        );
    }

    return (
        <Button
            variant="subtle"
            color="gray"
            fullWidth
            justify="flex-start"
            leftSection={icon}
            onClick={onClick}
            aria-label={label}
            bdrs={0}
        >
            Back
        </Button>
    );
};

/**
 * Tracks independent mobile and desktop positions within the submenu hierarchy.
 *
 * Postconditions: Desktop resets to the deepest level when hierarchy changes or outside clicks
 * occur while viewing a parent. Mobile resets while closed, and can step back to main navigation.
 * State changes are requested for later renders; no route navigation is performed by back buttons.
 */
const SubMenus = ({
    subMenus,
    collapsed,
    menuItems = [],
    mobileOpened,
    onNavigate
}: ISubMenusProps) => {
    const lowestSubMenuIndex = subMenus.length - 1;
    const [mobileSubMenuIndex, setMobileSubMenuIndex] = useState(lowestSubMenuIndex);
    const [desktopSubMenuIndex, setDesktopSubMenuIndex] = useState(lowestSubMenuIndex);
    const subMenuHierarchyKey = subMenus.map(subMenu => subMenu.code).join("|");
    const mobileSubMenu = subMenus[mobileSubMenuIndex];
    const desktopSubMenu = subMenus[desktopSubMenuIndex];

    const desktopRef = useClickOutside<HTMLDivElement>(
        () => setDesktopSubMenuIndex(lowestSubMenuIndex),
        undefined,
        undefined,
        desktopSubMenuIndex !== lowestSubMenuIndex
    );

    useEffect(
        () => {
            setDesktopSubMenuIndex(lowestSubMenuIndex);
        },
        [lowestSubMenuIndex, subMenuHierarchyKey]
    );

    useEffect(
        () => {
            if (!mobileOpened) {
                setMobileSubMenuIndex(lowestSubMenuIndex);
            }
        },
        [lowestSubMenuIndex, mobileOpened, subMenuHierarchyKey]
    );

    const previousMobileSubMenu = subMenus[mobileSubMenuIndex - 1];
    const mobileBackLabel = previousMobileSubMenu?.title
        ? `Back to ${previousMobileSubMenu.title}`
        : mobileSubMenuIndex === 0
          ? "Back to main navigation"
          : "Back to previous menu";
    const previousDesktopSubMenu = subMenus[desktopSubMenuIndex - 1];
    const desktopBackLabel = previousDesktopSubMenu?.title
        ? `Back to ${previousDesktopSubMenu.title}`
        : "Back to previous menu";

    return (
        <>
            <Box
                hiddenFrom="sm"
                h="100%"
            >
                <ScrollArea h="100%">
                    {mobileSubMenuIndex < 0 || !mobileSubMenu ? (
                        <NavMenu
                            mobile
                            menuItems={menuItems}
                            onNavigate={onNavigate}
                        />
                    ) : (
                        <>
                            <BackButton
                                label={mobileBackLabel}
                                onClick={() => setMobileSubMenuIndex(current => current - 1)}
                            />
                            <Divider />
                            <SubMenu
                                title={mobileSubMenu.title}
                                menuItems={mobileSubMenu.items}
                                context={mobileSubMenu.context}
                                onNavigate={onNavigate}
                            />
                        </>
                    )}
                </ScrollArea>
            </Box>
            <Box
                ref={desktopRef}
                visibleFrom="sm"
                h="100%"
            >
                <HelpTourTarget id={helpTourTargets.navigation}>
                    <ScrollArea
                        h="100%"
                        styles={{
                            content: {
                                display: "flex",
                                flexDirection: "column",
                                minHeight: "100%"
                            }
                        }}
                    >
                        {desktopSubMenu && (
                            <SubMenu
                                title={desktopSubMenu.title}
                                menuItems={desktopSubMenu.items}
                                context={desktopSubMenu.context}
                                collapsed={collapsed}
                            />
                        )}
                        {desktopSubMenuIndex > 0 && (
                            <Box mt="auto">
                                <Divider />
                                <BackButton
                                    label={desktopBackLabel}
                                    collapsed={collapsed}
                                    onClick={() =>
                                        setDesktopSubMenuIndex(current => Math.max(0, current - 1))
                                    }
                                />
                            </Box>
                        )}
                    </ScrollArea>
                </HelpTourTarget>
            </Box>
        </>
    );
};

export default SubMenus;
